import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface Program {
  _id: string;
  name: string;
  description?: string;
  status?: "active" | "inactive" | string;
  parentDepartmentId?: string;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

export type EditProgramPayload = {
  name?: string;
  description?: string;
  status?: "active" | "inactive" | string;
  parentDepartmentId?: string;
  members?: string[];
  timezone?: string;
};

export type ProgramResponse = {
  success: boolean;
  message: string;
  data: Program;
};

const editProgram = async (
  id: string,
  payload: EditProgramPayload
): Promise<ProgramResponse> => {
  const { data } = await customAxios.put<ProgramResponse>(
    `/programs/program/${id}`,
    payload
  );
  return data;
};

type EditProgramMutationArg = {
  id: string;
  payload: EditProgramPayload;
};

export const useEditProgram = () => {
  const queryClient = useQueryClient();

  return useMutation<ProgramResponse, AxiosError, EditProgramMutationArg>({
    mutationFn: ({ id, payload }) => editProgram(id, payload),
    onSuccess: async (data) => {
      showNotification({
        title: "Success",
        message: data.message,
        color: "green",
      });

      await queryClient.invalidateQueries(["programs"]);
    },
    onError: (error) =>
      showNotification({
        title: "Error",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to update program",
        color: "red",
      }),
  });
};
